/**
 * Secrets (data/secrets.ts, docs/systems/FUN_P1.md section 2): vistas and chests found by standing in them. A vista pays its
 * แต้มฝึกปรือ once. A chest pays its full reward the first time, then refills once per game day with a quarter of its
 * ตำลึง. What is found saves in `progress.secrets`. The event `secret` tells the client (a pin, a line on screen).
 */
import { DAY_SECONDS } from '../data/time';
import { CHEST_REWARDS, REGION_SECRETS, SECRET, SECRETS, secretById } from '../data/secrets';
import { floorAt, PX_METRES } from '../map';
import { coreOf, toCore } from '../region/core';
import type { SystemHost, WorldSystem } from './types';

/** Event (part of `WorldEvent`). `refill`: a chest's daily refill, not its first opening. */
export type SecretEvent = { type: 'secret'; id: string; kind: 'vista' | 'chest' | 'master' | 'lantern'; refill: boolean; gold: number; cultivation: number };

/** What the save keeps: the vistas seen, and the game day each chest was last opened on. */
export interface SecretsSave {
  readonly vistas: readonly string[];
  readonly chests: Readonly<Record<string, number>>;
  /** The hidden master's test is passed (systems/hiddenMaster.ts). */
  readonly master?: boolean;
}

export function parseSecrets(raw: unknown): SecretsSave {
  const r = (raw && typeof raw === 'object' ? raw : {}) as { vistas?: unknown; chests?: unknown; master?: unknown };
  const vistas = Array.isArray(r.vistas)
    ? [...new Set(r.vistas.filter((x): x is string => typeof x === 'string' && secretById(x)?.kind === 'vista'))]
    : [];
  const chests: Record<string, number> = {};
  if (r.chests && typeof r.chests === 'object')
    for (const [id, d] of Object.entries(r.chests as Record<string, unknown>))
      if (secretById(id)?.kind === 'chest' && typeof d === 'number' && Number.isFinite(d)) chests[id] = d;
  return { vistas, chests, ...(r.master === true ? { master: true } : {}) };
}

export class SecretsSystem implements WorldSystem {
  readonly name = 'secrets';

  constructor(private readonly h: SystemHost) {}

  /** How many vistas this player has seen. */
  vistas(): number {
    return this.h.world.progress.secrets?.vistas.length ?? 0;
  }

  step(_dt: number): void {
    const w = this.h.world;
    const p = w.player;
    if (p.dead || p.jailT > 0 || p.mount) return;
    const alt = (floorAt(w.map, p.x, p.y) + p.z) * PX_METRES;
    const day = Math.floor(w.time / DAY_SECONDS);
    // The secrets are in the town's own metres (a region's core is offset).
    const at = toCore(w.map, p.x, p.y);
    // The region's own secrets are in region metres, and only a region map (a core offset) has them.
    const o = coreOf(w.map);
    const region = o.x !== 0 || o.y !== 0;
    const all = region ? [...SECRETS, ...REGION_SECRETS] : SECRETS;
    const vistaTotal = all.filter((x) => x.kind === 'vista').length;
    for (const s of all) {
      const here = REGION_SECRETS.includes(s) ? p : at;
      if (s.zone !== w.map.zone || Math.hypot(here.x - s.x, here.y - s.y) > SECRET.radius || Math.abs(alt - s.h) > SECRET.band) continue;
      const save = w.progress.secrets ?? { vistas: [], chests: {} };
      if (s.kind === 'vista') {
        if (save.vistas.includes(s.id)) continue;
        w.progress.secrets = { ...save, vistas: [...save.vistas, s.id] };
        this.h.grantReward({ cultivation: SECRET.vistaCultivation });
        this.h.emit({ type: 'secret', id: s.id, kind: 'vista', refill: false, gold: 0, cultivation: SECRET.vistaCultivation });
        this.h.notice(`${s.name} · จุดชมวิว ${w.progress.secrets.vistas.length}/${vistaTotal}`);
        continue;
      }
      const last = save.chests[s.id];
      if (last !== undefined && last >= day) continue;
      const first = last === undefined;
      const full = CHEST_REWARDS[s.tier ?? 1];
      const gold = first ? full.gold : Math.round(full.gold * SECRET.refillShare);
      const cultivation = first ? full.cultivation : 0;
      w.progress.secrets = { ...save, chests: { ...save.chests, [s.id]: day } };
      this.h.grantReward({ gold, ...(cultivation ? { cultivation } : {}) });
      this.h.emit({ type: 'secret', id: s.id, kind: 'chest', refill: !first, gold, cultivation });
      this.h.notice(`${s.name} · หีบสมบัติ ${first ? '' : '(เติมใหม่) '}${gold} ตำลึง`);
    }
  }
}
